import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { apiFetch } from '../lib/apiClient';

const STATUS_LABELS = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
  dismissed: 'Closed',
};

export default function OrderComplaintSection({ order, compact = false, onChanged }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [complaint, setComplaint] = useState(order?.complaint || null);

  useEffect(() => {
    setComplaint(order?.complaint || null);
  }, [order?.complaint]);

  if (complaint) {
    return (
      <div
        className={`${compact ? 'mt-3' : 'mt-6'} rounded-xl border border-amber-500/30 bg-amber-500/10 p-4`}
        data-testid={`order-complaint-${order.order_id}`}
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="font-medium flex items-center gap-2 text-sm">
            <Clock3 className="w-4 h-4" />
            Late-order report
          </p>
          <Badge variant="secondary">{STATUS_LABELS[complaint.status] || complaint.status}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{complaint.message}</p>
        {complaint.response && (
          <div className="mt-3 rounded-lg bg-background/70 p-3 text-sm">
            <p className="font-medium mb-1">Response</p>
            <p className="text-muted-foreground">{complaint.response}</p>
          </div>
        )}
      </div>
    );
  }

  if (!order?.complaint_eligible) return null;

  const submit = async () => {
    const details = message.trim();
    if (details.length < 3) {
      toast.error('Please briefly describe what happened');
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiFetch(`/api/orders/${order.order_id}/complaints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'late', message: details }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not submit your report');
      setComplaint(data);
      setOpen(false);
      setMessage('');
      toast.success('Late-order report submitted');
      onChanged?.(data);
    } catch (error) {
      toast.error(error.message || 'Could not submit your report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`${compact ? 'mt-3' : 'mt-6'} rounded-xl border p-4`} data-testid={`late-report-${order.order_id}`}>
      {!open ? (
        <Button
          type="button"
          variant="outline"
          className="w-full text-amber-700 border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-800"
          onClick={() => setOpen(true)}
          data-testid={`report-late-btn-${order.order_id}`}
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Report late order
        </Button>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="font-medium text-sm">What happened?</p>
            <p className="text-xs text-muted-foreground">
              The vendor and SkipdQ support can see this report. This does not automatically issue a refund.
            </p>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="For example: My pickup time was 12:30 and the order was not ready…"
            className="w-full rounded-xl bg-muted border-0 p-3 text-base resize-none"
            data-testid={`complaint-message-${order.order_id}`}
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" className="flex-1" onClick={submit} disabled={submitting} data-testid={`submit-complaint-${order.order_id}`}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting…' : 'Submit report'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
